import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ChevronRightIcon } from 'lucide-react'
import { useState } from 'react'
import { DeltaInline } from '@/components/delta'
import { EmptyState } from '@/components/empty-state'
import { Pager } from '@/components/pager'
import { Panel } from '@/components/panel'
import { SerpStar } from '@/components/serp-star'
import { Button } from '@/components/ui/button'
import { formatDate, shortenUrl } from '@/lib/format'
import { beyondLabel, marketLabel, type TrackedKeyword, useTrackedKeywords } from '@/lib/serp'
import { cn } from '@/lib/utils'
import { SectionHeader, TableSkeleton, useSiteScope } from '../../-section'

export const Route = createFileRoute('/_app/$org/$host/search/_list/serp')({
  component: SerpPage,
})

const PAGE_SIZE = 25

/**
 * Where the site actually sits on the results page, for the keywords somebody
 * starred.
 *
 * Not read over a window, which is why this page never asks the bar for one: a
 * ranking is a state and narrowing it to seven days would not make the answer
 * smaller in any sense the reader meant. Each row carries the date of the
 * reading it is quoting instead, because a position with no date beside it is a
 * number nobody can age.
 *
 * The list is short by construction — it is what somebody chose — so it arrives
 * whole and pages in the browser rather than asking again per screenful.
 */
function SerpPage() {
  const { org, host, range, site } = useSiteScope()
  const navigate = useNavigate()
  const { data, isPending, isFetching } = useTrackedKeywords(site)
  const [offset, setOffset] = useState(0)

  if (isPending && !data) {
    return (
      <>
        <SectionHeader title="SERP" />
        <TableSkeleton />
      </>
    )
  }

  const rows = data?.rows ?? []

  if (rows.length === 0) {
    return (
      <>
        <SectionHeader title="SERP" />
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            title="No keywords tracked"
            description={`Star a keyword and the results page it lands on is read for ${host}, then followed here.`}
            action={
              <Button asChild size="sm">
                <Link to="/$org/$host/keywords" params={{ org, host }} search={{ range }}>
                  Go to keywords
                </Link>
              </Button>
            }
          />
        </div>
      </>
    )
  }

  const page = rows.slice(offset, offset + PAGE_SIZE)

  return (
    <>
      <SectionHeader title="SERP" />

      <Panel flush className={cn(isFetching && 'opacity-70 transition-opacity')}>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <th className="py-2 pr-3 pl-4 text-left">
                  <span className="mono-label text-muted-foreground">Keyword</span>
                </th>
                <th className="w-[96px] px-3 py-2 text-right">
                  <span className="mono-label text-muted-foreground">Rank</span>
                </th>
                <th className="w-[220px] px-3 py-2 text-left">
                  <span className="mono-label text-muted-foreground">Ranking URL</span>
                </th>
                <th className="w-[150px] px-3 py-2 text-left">
                  <span className="mono-label text-muted-foreground">Market</span>
                </th>
                <th className="w-[96px] px-3 py-2 text-right">
                  <span className="mono-label text-muted-foreground">Read</span>
                </th>
                <th className="w-14" aria-label="Open" />
              </tr>
            </thead>

            <tbody>
              {page.map((row) => (
                <Row
                  key={`${row.keywordId}-${marketLabel(row.market)}`}
                  row={row}
                  host={host}
                  site={site}
                  onSelect={
                    row.queryId === null
                      ? undefined
                      : () =>
                          navigate({
                            to: '/$org/$host/search/serp/$queryId',
                            params: { org, host, queryId: String(row.queryId) },
                            search: { range },
                          })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>

        {rows.length > PAGE_SIZE && (
          <div className="flex items-center border-t border-border px-4 py-2.5">
            <Pager
              offset={offset}
              limit={PAGE_SIZE}
              total={rows.length}
              onOffsetChange={setOffset}
              className="ml-auto"
            />
          </div>
        )}
      </Panel>
    </>
  )
}

function Row({
  row,
  host,
  site,
  onSelect,
}: {
  row: TrackedKeyword
  host: string
  site: ReturnType<typeof useSiteScope>['site']
  onSelect?: () => void
}) {
  const { check } = row
  // Position falls as a site rises, so the sign is inverted for the reader in
  // the same way the breakdown tables invert it.
  const move =
    check?.position != null && check.previous?.position != null
      ? check.position - check.previous.position
      : null

  return (
    <tr
      className={cn(
        'group border-b border-border last:border-b-0 hover:bg-secondary/40',
        onSelect && 'cursor-pointer',
      )}
      onClick={onSelect}
    >
      <td className="max-w-0 py-2.5 pr-3 pl-4">
        <span className="block truncate font-mono text-[12.5px]" title={row.text}>
          {row.text}
        </span>
      </td>

      <td className="px-3 py-2.5 text-right">
        {check === null ? (
          <span className="font-mono text-[11px] text-muted-foreground/70">reading</span>
        ) : check.position === null ? (
          // A reading that covered one page cannot say a site ranks nowhere,
          // only that it was not in the first ten. So it says that.
          <span className="font-mono text-[12.5px] text-muted-foreground tabular">
            {beyondLabel(check.pages)}
          </span>
        ) : (
          <span className="inline-flex items-baseline gap-1.5">
            <span className="font-mono text-[13px] tabular">{check.position}</span>
            <DeltaInline value={move} higherIsBetter={false} absolute digits={0} />
          </span>
        )}
      </td>

      <td className="max-w-0 px-3 py-2.5">
        <span
          className="block truncate font-mono text-[12px] text-muted-foreground"
          title={check?.url ?? undefined}
        >
          {check?.url ? shortenUrl(check.url, host) : '—'}
        </span>
      </td>

      <td className="px-3 py-2.5">
        <span className="font-mono text-[11px] text-muted-foreground">
          {marketLabel(row.market)}
        </span>
      </td>

      <td className="px-3 py-2.5 text-right">
        <span className="font-mono text-[11px] text-muted-foreground tabular">
          {check ? formatDate(check.date) : '—'}
        </span>
      </td>

      <td className="pr-3 text-right whitespace-nowrap">
        {/* Every row here is starred, so the star is only ever the way out of
            the list — it stays out of the way until the row is pointed at. */}
        {row.queryId !== null && (
          <span className="opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <SerpStar
              site={site}
              host={host}
              queryId={row.queryId}
              keyword={row.text}
              tracked
              className="align-middle"
            />
          </span>
        )}
        {onSelect && (
          <ChevronRightIcon
            className="inline size-3.5 align-middle text-muted-foreground/40 transition-colors group-hover:text-plum"
            aria-hidden
          />
        )}
      </td>
    </tr>
  )
}
